import React, { Fragment } from 'react'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { useApi } from '../../../hooks/useApi'
import { TypeVasCreate } from '../../../interfaces/TypeVas/TypeVas'
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { Field, FieldProps, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Loading } from '../../../components/UI'
import { Status } from '../../../hooks/useFetchState'
import { createTypeVas } from '../config/api'

interface CreateTypeVasModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestTypeVas: (filters?: Filter[], page?: number, pageSize?: number) => void
}

const initialValues: TypeVasCreate = {
  name: '',
  description: '',
  active: true
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(1, 'el campo Código debe tener entre 1 y 50 caracteres')
    .max(50, 'el campo Código debe tener entre 1 y 50 caracteres')
    .required('el campo Código debe tener entre 1 y 50 caracteres'),
  description: Yup.string()
    .trim()
    .min(1, 'el campo Descripción debe tener entre 1 y 50 caracteres')
    .max(50, 'el campo Descripción debe tener entre 1 y 50 caracteres')
    .required('el campo Descripción debe tener entre 1 y 50 caracteres')
})

export const CreateTypeVasModal: React.FC<CreateTypeVasModalProps> = ({
  handleSnackbar,
  handleCloseModal,
  requestTypeVas
}) => {
  const { state, callApi } = useApi()

  // Se devuelve la promesa para que Formik resetee `isSubmitting` cuando la
  // request termina; si no, el botón queda en 'Procesando...' tras un error.
  const handleSubmit = (values: TypeVasCreate) => {
    const payload: TypeVasCreate = {
      active: values.active,
      description: values.description.trim(),
      name: values.name.trim()
    }

    return callApi(createTypeVas(payload))
      .then(() => {
        requestTypeVas()
        handleSnackbar('Entidad creada correctamente', true)
        handleCloseModal()
      })
      .catch((error) => {
        handleSnackbar('Error al crear: ' + error, false)
      })
  }

  if (state.status === Status.Loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isValid, dirty, isSubmitting }) => (
          <Form autoComplete='off'>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Field name='name'>
                  {({ field, meta }: FieldProps<string>) => (
                    <TextField
                      {...field}
                      id='Name'
                      label='Código'
                      variant='outlined'
                      fullWidth
                      placeholder='EJ. SERIAL_NUMBER'
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>

              <Grid size={12}>
                <Field name='description'>
                  {({ field, meta }: FieldProps<string>) => (
                    <TextField
                      {...field}
                      id='Description'
                      label='Descripción'
                      variant='outlined'
                      fullWidth
                      placeholder='Descripción del servicio'
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>

              <Grid size={12}>
                <Paper
                  variant='outlined'
                  style={{
                    width: '96%',
                    padding: '10px 10px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    {/* Label a la izquierda */}
                    <Typography
                      variant='subtitle1'
                      style={{ fontWeight: 600, width: 80 }}
                    >
                      Estado
                    </Typography>

                    {/* Switch + Activo/Inactivo */}
                    <FormControlLabel
                      style={{ marginLeft: 10 }}
                      label={
                        <Typography
                          style={{
                            fontWeight: 600,
                            color: values.active ? '#4caf50' : '#757575'
                          }}
                        >
                          {values.active ? 'Activo' : 'Inactivo'}
                        </Typography>
                      }
                      control={
                        <Switch
                          checked={values.active}
                          onChange={(e) =>
                            setFieldValue('active', e.target.checked)
                          }
                          color='primary'
                        />
                      }
                    />
                  </div>

                  {/* Texto inferior aclaratorio */}
                  <Typography
                    variant='caption'
                    style={{
                      marginTop: 8,
                      color: '#888'
                    }}
                  >
                    Activar o desactivar este tipo de VAS
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={12} display='flex' gap={2} justifyContent='flex-end'>
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  sx={{ borderRadius: '12px' }}
                >
                  Cancelar
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={!isValid || !dirty || isSubmitting}
                  sx={{ borderRadius: '12px' }}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : undefined
                  }
                >
                  {isSubmitting ? 'Procesando...' : 'Guardar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
}
